import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-col justify-between w-[45%] bg-indigo-600 text-white p-12 relative overflow-hidden">
        <div className="flex items-center gap-3 text-xl font-head font-bold">
          <img src="/exlibris-logo.jpg" alt="Ex Libris" className="h-12 w-12 rounded-full object-cover ring-2 ring-white/40" /> Ex Libris
        </div>
        <div>
          <h1 className="text-5xl font-head font-extrabold leading-tight tracking-tight">
            Your personal reading library.
          </h1>
          <p className="mt-4 text-indigo-100 text-lg max-w-md">
            Read, highlight, annotate and lend your books — all in one focused workspace.
          </p>
        </div>
        <div className="text-indigo-200 text-sm">Highlight · Annotate · Lend · Organize</div>
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-indigo-500/40 blur-3xl" />
      </div>
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-sm">
          <h2 className="text-3xl font-head font-bold text-slate-900">{title}</h2>
          <p className="text-slate-500 mt-1 mb-8">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (user) nav("/");
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (e) {
      setErr(apiErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Welcome back" subtitle="Sign in to your library">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="login-email" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} required className="mt-1" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" data-testid="login-password" type="password" value={password}
            onChange={(e) => setPassword(e.target.value)} required className="mt-1" />
        </div>
        {err && <p className="text-sm text-red-600" data-testid="login-error">{err}</p>}
        <Button data-testid="login-submit" type="submit" className="w-full" disabled={loading}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
        </Button>
      </form>
      <p className="text-sm text-slate-500 mt-6">
        New here?{" "}
        <button className="text-indigo-600 font-medium hover:underline" onClick={() => nav("/register")}>
          Create the owner account
        </button>
      </p>
    </Shell>
  );
}

export function RegisterPage() {
  const { register, user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (user) nav("/");
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(form.email, form.password, form.name);
      nav("/");
    } catch (e) {
      setErr(apiErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Create your account" subtitle="The first account becomes the library owner (admin)">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="name">Name</Label>
          <Input id="name" data-testid="register-name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="register-email" type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" data-testid="register-password" type="password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1" />
        </div>
        {err && <p className="text-sm text-red-600" data-testid="register-error">{err}</p>}
        <Button data-testid="register-submit" type="submit" className="w-full" disabled={loading}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Create account
        </Button>
      </form>
      <p className="text-sm text-slate-500 mt-6">
        Already have an account?{" "}
        <button className="text-indigo-600 font-medium hover:underline" onClick={() => nav("/login")}>
          Sign in
        </button>
      </p>
    </Shell>
  );
}

export function InvitePage({ token }) {
  const { registerInvite, user } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ name: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/${token}`).then((r) => setInfo(r.data)).catch(() => setErr("Invite not found or expired."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await registerInvite(token, form.password, form.name);
      nav("/");
    } catch (e) {
      setErr(apiErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="You've been lent a book" subtitle={info ? `${info.owner_name} is sharing with you` : "Loading invite…"}>
      {info && (
        <div className="mb-6 p-4 rounded-xl bg-indigo-50 border border-indigo-100" data-testid="invite-book-info">
          <p className="text-sm text-slate-500">Borrowing for {info.weeks} weeks</p>
          <p className="font-head font-bold text-lg text-slate-900">{info.book_title}</p>
          {info.book_author && <p className="text-slate-600 text-sm">by {info.book_author}</p>}
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="name">Your name</Label>
          <Input id="name" data-testid="invite-name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" />
        </div>
        <div>
          <Label htmlFor="password">Choose a password</Label>
          <Input id="password" data-testid="invite-password" type="password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1" />
        </div>
        {err && <p className="text-sm text-red-600" data-testid="invite-error">{err}</p>}
        <Button data-testid="invite-submit" type="submit" className="w-full" disabled={loading || !info}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Accept & start reading
        </Button>
      </form>
    </Shell>
  );
}
